import { FC, ReactNode, useMemo } from 'react';
import { Interpolation, Theme } from '@emotion/react';
import { CallIntelTypes } from '@frontend/api-call-intel';
import { Trans, useTranslation } from '@frontend/i18n';
import { Text, TextLink } from '@frontend/design-system';
import { PromoBanner } from '../../../common/components/promo-banners';
import { TSS_HELP_LINK } from '../../constants';
import { useCallIntelInAppBannerVisibility } from '../../hooks/use-call-intel-in-app-banner-visibility';
import { getInAppAwarenessBannerTrackingId } from '../../tracking-ids';
import { InAppAwarenessPromoSource } from '../../types';

interface TSSRecoveredOpportunityBannerProps {
  title?: ReactNode;
  styles?: Interpolation<Theme>;
  locationId?: string;
  isDemo?: boolean;
  callInsights?: Pick<CallIntelTypes.CallRecord, 'schedulingOutcomeV2' | 'isFieldEdited'>;
}

const COMPONENT_ID: InAppAwarenessPromoSource = 'call-insight-unscheduled-opportunity';

export const TSSUnscheduledBanner: FC<TSSRecoveredOpportunityBannerProps> = ({
  title,
  locationId,
  isDemo,
  callInsights,
  styles,
}) => {
  const { t } = useTranslation('highValueAdoption', { keyPrefix: 'call-intel' });

  const bannerLocationIds = useMemo(() => (locationId ? [locationId] : []), [locationId]);

  const { ref, handleDismiss, shouldShowBanner, isBetaBanner } = useCallIntelInAppBannerVisibility({
    locationIds: bannerLocationIds,
    isDemo,
    componentId: COMPONENT_ID,
    enabled:
      !!callInsights &&
      !callInsights.isFieldEdited?.isSchedulingOutcomeEdited &&
      callInsights.schedulingOutcomeV2 === CallIntelTypes.SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_UNSCHEDULED,
    maxVisibilityCount: 1,
    maxInteractionCount: 0,
  });

  if (!shouldShowBanner) {
    return null;
  }
  return (
    <PromoBanner
      badge={isBetaBanner ? 'beta' : 'new'}
      containerStyles={styles}
      innerRef={ref}
      title={
        title || (
          <Text size='medium'>
            <Trans t={t}>
              Update the <strong>Call Analysis</strong> by marking calls as <strong>Recovered Opportunity</strong> when
              an appointment has since been scheduled.{' '}
              <TextLink
                target='_blank'
                rel='noopener noreferrer'
                href={TSS_HELP_LINK}
                trackingId={getInAppAwarenessBannerTrackingId({
                  source: 'call-insight-unscheduled-opportunity',
                  isBeta: isBetaBanner,
                  action: 'learn-more',
                })}
              >
                Learn More
              </TextLink>
            </Trans>
          </Text>
        )
      }
      dismissTrackingId={getInAppAwarenessBannerTrackingId({
        source: COMPONENT_ID,
        isBeta: isBetaBanner,
        action: 'dismiss',
      })}
      onDismiss={handleDismiss}
      color={isBetaBanner ? 'indigo' : 'seaweed'}
      responsiveActions={false}
    />
  );
};
